import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Tag, PencilRuler, FlaskConical, Shirt, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { PRODUCTS, IMAGES } from "@/data/products";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function OemPrivateLabel() {
  const oemStyles = PRODUCTS.filter((p) => p.category === "oem");

  return (
    <div data-testid="oem-page">
      <Seo
        title="OEM & Private Label Denim — Shineni India, Faridabad"
        description="Private-label denim jeans manufacturing in Faridabad: your tech pack, your labels, our production floor. Sampling to bulk production for fashion brands."
      />

      <section className="relative border-b border-border">
        <img src={IMAGES.folded} alt="Folded private-label denim jeans — sample imagery" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-indigo-deep/75" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Your brand · Our factory</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">
            OEM &amp; private label denim
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Launch or extend your denim line without building a factory. We produce to your
            specifications — fits, washes, trims, labels and packaging — under your brand.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: PencilRuler, t: "You bring the spec", d: "Tech pack, reference samples or even a sketch — we translate it into production-ready patterns." },
            { icon: FlaskConical, t: "Sampling & wash development", d: "Fit samples and wash references developed and refined until you approve." },
            { icon: Shirt, t: "Bulk production", d: "Approved samples go into bulk production with your labels, tags and packaging." },
            { icon: Tag, t: "Your brand, delivered", d: "Finished, checked and packed garments ready for your warehouse or onward shipment." },
          ].map((s) => (
            <motion.div key={s.t} {...fade} className="border border-border bg-white p-8">
              <s.icon className="h-7 w-7 text-gold" />
              <h2 className="mt-5 font-display text-lg font-semibold text-indigo">{s.t}</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground/70">{s.d}</p>
            </motion.div>
          ))}
        </div>

        <motion.h2 {...fade} className="mt-20 font-display text-2xl font-semibold text-indigo sm:text-3xl">
          Sample program styles
        </motion.h2>
        <motion.p {...fade} className="mt-3 max-w-2xl text-sm text-foreground/70">
          Representative samples of how private-label programs are structured. All imagery is sample imagery.
        </motion.p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {oemStyles.map((p) => (
            <motion.div key={p.id} {...fade}>
              <ProductCard product={p} />
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} className="mt-16 rounded-sm bg-indigo p-8 text-white sm:p-12">
          <h2 className="font-display text-2xl font-semibold sm:text-3xl">Start your private-label program</h2>
          <p className="mt-3 max-w-2xl text-sm text-white/75">
            Tell us your target styles, quantities and destination. Minimums and pricing are quoted
            per program after we review your specifications.
          </p>
          <Link to="/request-a-quote" data-testid="oem-quote-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-sm bg-gold px-8 py-3.5 text-sm font-semibold text-indigo-deep transition-colors hover:bg-gold-dark">
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
